/**
 * =============================================================================
 * SPEQ55 RFP Pro - Pricing Page
 * =============================================================================
 *
 * Public pricing page with plan comparison.
 *
 * @module pages/public/Pricing
 * @author SPEQ55
 */

import { Link } from 'react-router-dom';

/**
 * Pricing plans data.
 */
const plans = [
  {
    name: 'Monthly',
    price: '$70',
    period: 'per month',
    description: '',
    features: ['Up to 20 RFPs per month', 'Email support'],
    cta: 'Contact Sales',
    ctaLink: '/contact',
    highlighted: false,
  },
  {
    name: 'Yearly',
    price: '$55',
    period: 'per month',
    description: '',
    features: ['Up to 20 RFPs per month', 'Email support'],
    cta: 'Contact Sales',
    ctaLink: '/contact',
    highlighted: true,
    badge: 'Annual Discount',
  },
  {
    name: 'Enterprise',
    price: 'Custom',
    priceSmall: true,
    period: 'pricing',
    description: '',
    features: ['Volume discounts', 'Dedicated account manager', 'Custom integrations'],
    cta: 'Contact Us',
    ctaLink: '/contact',
    highlighted: false,
  },
];

/**
 * Pricing page component.
 *
 * @returns {JSX.Element} Pricing page
 */
function Pricing() {
  return (
    <div>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-900 via-primary-800 to-primary-900 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-extrabold text-white">
            Simple, Transparent Pricing
          </h1>
          <p className="mt-6 text-xl text-primary-100 max-w-3xl mx-auto">
            Choose the plan that fits your needs.
          </p>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-20 bg-accent-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {plans.map((plan, index) => (
              <div
                key={index}
                className={`
                  relative bg-white rounded-2xl shadow-sm
                  ${
                    plan.highlighted
                      ? 'ring-2 ring-primary-500 shadow-lg scale-105'
                      : 'border border-accent-200'
                  }
                `}
              >
                {/* Badge */}
                {plan.badge && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="inline-flex items-center px-4 py-1 rounded-full text-sm font-medium bg-primary-500 text-white">
                      {plan.badge}
                    </span>
                  </div>
                )}

                <div className="p-8">
                  {/* Plan Header */}
                  <h3 className="text-xl font-semibold text-accent-900">{plan.name}</h3>
                  {plan.description && (
                    <p className="mt-2 text-sm text-accent-600">{plan.description}</p>
                  )}

                  {/* Price */}
                  <div className="mt-6">
                    <span
                      className={`font-extrabold text-accent-900 ${plan.priceSmall ? 'text-2xl' : 'text-4xl'}`}
                    >
                      {plan.price}
                    </span>
                    <span className="text-accent-500 ml-2">{plan.period}</span>
                  </div>

                  {/* Features */}
                  <ul className="mt-8 space-y-4">
                    {plan.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-start">
                        <svg
                          className="h-5 w-5 text-secondary-500 flex-shrink-0 mt-0.5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                        <span className="ml-3 text-sm text-accent-600">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {/* CTA Button */}
                  <Link
                    to={plan.ctaLink}
                    className={`
                      mt-8 block w-full py-3 px-4 rounded-md text-center font-medium transition-colors duration-200
                      ${
                        plan.highlighted
                          ? 'bg-primary-600 text-white hover:bg-primary-700'
                          : 'bg-primary-50 text-primary-700 hover:bg-primary-100'
                      }
                    `}
                  >
                    {plan.cta}
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Have a Question?</h2>
          <p className="text-lg text-primary-100 mb-8 max-w-2xl mx-auto">
            Our team is here to help. Reach out for a personalized demo or to discuss your specific
            needs.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-primary-600 bg-white hover:bg-primary-50 transition-colors duration-200"
          >
            Contact Us
          </Link>
        </div>
      </section>
    </div>
  );
}

export default Pricing;
